/**
 * API client.
 *
 * The session is an HttpOnly cookie the browser sends automatically; no token is
 * ever held in JavaScript. State-changing requests echo the CSRF cookie back in a
 * header, which is the other half of the double-submit check the API performs.
 */

export interface ApiErrorBody {
  error: { code: string; message: string };
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)hugarheim_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

/** Authenticated request against /api/v1 (cookie session, CSRF echo, typed ApiError). Shared by feature API modules. */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  if (method !== 'GET' && method !== 'HEAD') headers.set('X-CSRF-Token', csrfToken());

  const response = await fetch(`/api/v1${path}`, {
    ...init,
    method,
    headers,
    credentials: 'include',
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!response.ok) {
    let code = 'unknown';
    let message = text || response.statusText;
    try {
      const body = JSON.parse(text) as ApiErrorBody;
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      /* a non-JSON error body is used as-is */
    }
    throw new ApiError(response.status, code, message);
  }
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

// ---------------------------------------------------------------- types

import type {
  ChangesView,
  HistoryDay,
  Entity,
  EntityInput,
  EntityRelation,
  LookupResult,
  QualityCase,
  QualityCaseDetail,
  QualityCaseStatus,
  RelationKind,
  SubjectPage,
  SubjectSummary,
} from './waveTypes';
export type * from './waveTypes';

export type CollectionStatus = 'success' | 'empty' | 'partial' | 'failed';

export interface Me {
  id: string;
  username: string;
  email?: string;
  csrf_token?: string;
  expires_at?: string;
}

export type SourceRole = 'journalism' | 'official' | 'parliament' | 'index' | 'events';

export type ArticleType =
  | 'report'
  | 'opinion'
  | 'interview'
  | 'commentary'
  | 'debate'
  | 'official_communication'
  | 'parliamentary_record';

/** Who wrote or issued an item and how it reached us. Every field is optional. */
export interface Attribution {
  byline?: string | null;
  article_type?: ArticleType | null;
  categories?: string[] | null;
  issuer?: string | null;
  document_id?: string | null;
  procedural_stage?: string | null;
  affair_id?: string | null;
  council?: string | null;
  event_date?: string | null;
  original_publisher?: string | null;
  original_url?: string | null;
  discovered_via?: string | null;
  republished_from?: string | null;
  date_precision?: string | null;
  terms_attribution?: string | null;
}

export interface Evidence {
  attribution?: Attribution | null;
  /** When this revision was retrieved from the source (RFC 3339). */
  fetched_at?: string | null;
  source_id: string;
  source_name?: string;
  item_id: string;
  revision_id: string;
  title: string;
  url: string;
  published_at?: string | null;
  text_source: 'full_text' | 'excerpt' | 'none';
}

export interface NewsPayload {
  payload_type: 'news.v1';
  summary: string;
  key_points?: string[];
  source_additions?: Array<{ source_id: string; addition: string }>;
  /** From producer 0.2.0: open questions about the story only. Older versions also carry coverage notes here. */
  unknowns?: string[];
  /** From producer 0.2.0: how much of each source was read. `note` is German and reader-facing. */
  coverage?: Array<{ source_id: string; text_source: 'full_text' | 'excerpt' | 'none'; note: string; detail?: string }>;
  language: string;
  sources: Array<{
    source_id: string;
    title: string;
    url: string;
    published_at?: string | null;
    text_source: string;
    article_type?: ArticleType | null;
    issuer?: string | null;
    procedural_stage?: string | null;
  }>;
  degraded?: boolean;
}

export interface CrossFitClass {
  class_key: string;
  time_start: string;
  time_end?: string | null;
  class_type: string;
  coach?: string | null;
  own_status: 'reserved' | 'waitlisted';
  roster_status: 'complete' | 'partial' | 'unavailable';
  capacity?: number | null;
  // null means the roster was never read; [] means it was read and is empty.
  // The two must stay distinguishable all the way to the screen.
  participants?: Array<{ display_name: string; position: number }> | null;
  waitlist?: Array<{ display_name: string; position: number }> | null;
  own_waitlist_position?: number | null;
  workout?: string | null;
  missing_fields?: string[];
}

/**
 * Both payload versions are rendered. v1 is what the browser reader produces and
 * remains the rollback path, so the reader must keep working if a release goes
 * back to it; v2 is what the phone capture produces and carries the day's
 * workout, the gym's class times and bookings on later dates.
 */
export interface CrossFitPayload {
  payload_type: 'crossfit.v1' | 'crossfit.v2';
  local_date: string;
  collection_status: CollectionStatus;
  collected_at?: string | null;
  /** When the source was observed, as opposed to when the collector ran. */
  captured_at?: string | null;
  capture_basis?: string | null;
  capture_approximate?: boolean;
  /** Which reading the workout and the own bookings come from (a later reading may not have read them). */
  workout_captured_at?: string | null;
  bookings_captured_at?: string | null;
  registration_state?: 'none' | 'reserved' | 'waitlisted' | 'mixed' | 'unknown' | null;
  stale?: boolean;
  stale_since?: string | null;
  workout?: { title?: string | null; body: string; displayed_date_text?: string | null } | null;
  schedule?: Array<{
    time_start: string;
    time_end?: string | null;
    class_type: string;
    duration_text?: string | null;
    filled?: number | null;
    capacity?: number | null;
    reserved?: boolean | null;
  }> | null;
  /** Required by the contract, but one early production card carried null. */
  classes: CrossFitClass[] | null;
  /** Bookings on later dates, each carrying its own date. */
  upcoming?: Array<CrossFitClass & { local_date: string }> | null;
  notes?: string[];
}

export type CardPayload = NewsPayload | CrossFitPayload;

export interface CardVersion {
  id: string;
  card_id: string;
  version: number;
  content_hash: string;
  title: string;
  summary: string;
  language: string;
  payload: CardPayload;
  payload_schema: string;
  evidence: Evidence[];
  producer: string;
  producer_version: string;
  model_run_id?: string | null;
  degraded: boolean;
  collection_status: CollectionStatus;
  freshness_at: string;
  created_at: string;
}

export interface Card {
  id: string;
  card_key: string;
  domain: string;
  section: string;
  privacy_class: string;
  subject_ref?: string | null;
}

export type Interest = 'more' | 'less';
export type Quality = 'correct' | 'incorrect';
export type QualityReason = 'summary_wrong' | 'stories_mixed' | 'missing_important' | 'nothing_new';

/**
 * Feedback on one exact card version (ADR-028). Interest is the only part that
 * moves ranking; quality is about Hugarheim's own processing and never does.
 * `thumb`/`assessment` are the pre-ADR-028 fields, no longer written.
 */
export interface Feedback {
  id: string;
  card_version_id: string;
  briefing_id?: string | null;
  interest?: Interest | null;
  interest_at?: string | null;
  quality?: Quality | null;
  quality_reasons?: QualityReason[] | null;
  note?: string | null;
  thumb?: 'up' | 'down' | null;
  assessment?: 'positive' | 'neutral' | 'negative' | null;
  updated_at: string;
}

export interface FeedbackResult {
  feedback: Feedback;
  /** What an interest rating applies to: topics and subjects, never a publisher. */
  interest_effect: Array<{ key: string; kind: 'topic' | 'entity'; label: string }>;
  quality_case_id?: string | null;
}

/**
 * The ranking score stored on a briefing entry.
 *
 * Every component is recorded by the backend so an ordering can be explained
 * afterwards. `group` and `group_label` are set on entries behind "Weitere": they
 * name the front-page story an entry follows up, or the source it came from, so a
 * long tail can be read as a few named groups instead of one flat list. Editions
 * published before grouping existed simply have neither.
 */
export interface EntryScore {
  total?: number;
  personal_fit?: number;
  importance?: number;
  novelty?: number;
  recency?: number;
  redundancy_penalty?: number;
  staleness_penalty?: number;
  exploration_slot?: boolean;
  explanation?: string;
  degraded?: boolean;
  group?: string;
  group_label?: string;
  /** Why the story is in this edition. Absent on editions before the slot schedule. */
  change?: EntryChange;
  /** Tracked subjects that were the story's main actor when the edition was built. */
  subjects?: Array<{ entity_id: string; name: string; via?: string }> | null;
  subject_boost?: number;
}

/** One cited report, as the change and the comparison name it. */
export interface SourceRef {
  source_id: string;
  source_name?: string;
  title: string;
  url?: string;
  item_id: string;
  published_at?: string | null;
}

/**
 * An edition's judgement of a story relative to what the reader was shown
 * before. `what_changed` is source text or model output, never composed here.
 */
export interface EntryChange {
  kind: 'new' | 'update' | 'context' | 'repeat';
  basis?: string;
  what_changed?: string[] | null;
  /** Reports added since the baseline. Listed apart: a source alone is not a development. */
  sources_added?: SourceRef[] | null;
  /** CrossFit: when the day's data was read. */
  captured_at?: string | null;
  previous_version_id?: string | null;
  previous_shown_at?: string | null;
  previous_edition?: string | null;
}

/**
 * A topic is source-level: each source names one in its catalogue config and a
 * card's topic follows from the sources of its evidence. `null` means none of
 * the sources declares one; such cards are grouped as "Weitere Themen".
 */
export interface Topic {
  id: string;
  label: string;
}

export interface CardView {
  entry?: {
    /** news, crossfit, events or context; absent from older backends. */
    section?: string;
    position: number;
    in_more: boolean;
    score?: EntryScore;
    reason?: string | null;
  };
  card: Card;
  version: CardVersion;
  feedback?: Feedback | null;
  following: boolean;
  follow_id?: string | null;
  freshness_age: string;
  /** Absent on responses from a backend that predates topics; treated as null. */
  topic?: Topic | null;
  /** The reader's tracked subjects found in this story now (main actor or mention). */
  subjects?: SubjectChip[] | null;
}

export interface SubjectChip {
  entity_id: string;
  name: string;
  role: 'main' | 'mention';
}

export interface BriefingCompleteness {
  partial?: boolean;
  reasons?: string[];
  section_counts?: Record<string, number>;
  section_visible?: Record<string, number>;
  source_freshness?: Record<string, string>;
  degraded_cards?: number;
  inference_healthy?: boolean;
  cutoff_at?: string;
  built_at?: string;
  /** Slot editions only. */
  slot?: string;
  scheduled_at?: string | null;
  coverage_from?: string | null;
  previous?: { id: string; edition_date: string; edition_type: string; published_at?: string | null; cutoff_at: string } | null;
  quiet?: boolean;
  delta?: {
    new: number;
    updates: number;
    context: number;
    repeats: number;
    immaterial: number;
    shared_item_duplicates: number;
  } | null;
  newest_content_at?: string | null;
  preparation_ms?: number;
}

/**
 * The slot key of an edition. Seven slots since the slot schedule; "morning" is
 * the legacy morning edition, which occupies the early-morning slot of its day.
 * Any other string is shown by its label from the API, never rejected.
 */
export type EditionType =
  | 'early_morning'
  | 'pre_market'
  | 'market_opening'
  | 'lunch'
  | 'afternoon'
  | 'market_close'
  | 'evening'
  | 'morning'
  | (string & {});

export interface Briefing {
  id: string;
  edition_date: string;
  edition_type: EditionType;
  timezone: string;
  revision: number;
  cutoff_at: string;
  published_at?: string | null;
  scheduled_at?: string | null;
  coverage_from?: string | null;
  status: string;
  completeness: BriefingCompleteness;
}

export interface EditionMeta {
  slot: string;
  label: string;
  at?: string;
  legacy: boolean;
}

export interface EditionLink {
  id: string;
  edition_date: string;
  edition_type: EditionType;
  label: string;
  published_at?: string | null;
}

export interface BriefingView {
  briefing: Briefing | null;
  /** Absent from backends before the slot schedule. */
  edition?: EditionMeta;
  navigation?: { previous: EditionLink | null; next: EditionLink | null; latest: EditionLink | null } | null;
  /** Newest edition only: what arrived after its cutoff and is therefore not in it. */
  since_cutoff?: {
    cutoff_at: string;
    news_versions: number;
    crossfit_version_id?: string | null;
    crossfit_at?: string | null;
    next_slot?: string;
    next_label?: string;
    next_at?: string | null;
  } | null;
  sections: Array<{ section: string; cards: CardView[] }>;
  more: CardView[];
  message?: string;
}

export interface SlotStatus {
  slot: string;
  label: string;
  at: string;
  scheduled_at: string;
  state: 'published' | 'due' | 'missed' | 'upcoming';
  briefing_id?: string | null;
  edition_type?: string;
  published_at?: string | null;
  quiet: boolean;
  partial: boolean;
}

export interface EditionDay {
  date: string;
  timezone: string;
  slots: SlotStatus[];
  /** Local dates with at least one edition, newest first. */
  dates: string[];
}

export interface Follow {
  id: string;
  subject_type: string;
  subject_id: string;
  label: string;
  /** active and stopped for every follow; paused for subjects. */
  status: 'active' | 'paused' | 'stopped';
  materiality?: { include_related?: string[] | null; telegram?: boolean } | null;
  last_checked_at?: string | null;
  last_changed_at?: string | null;
  last_check_status?: string | null;
  last_check_error?: string | null;
  created_at: string;
}

export interface FollowEvent {
  id: string;
  kind: string;
  detail: { reason?: string; error?: string };
  card_version_id?: string | null;
  created_at: string;
}

export interface ModelRun {
  id: string;
  task: string;
  provider: string;
  model: string;
  runtime?: string | null;
  prompt_version: string;
  schema_version: string;
  status: string;
  latency_ms?: number | null;
  error?: string | null;
  created_at: string;
}

export interface EvidenceView {
  card_id: string;
  version: number;
  producer: string;
  producer_version: string;
  evidence: Evidence[];
  degraded: boolean;
  collection_status: CollectionStatus;
  deterministic?: boolean;
  model_run?: ModelRun | null;
  revisions?: Array<{
    revision_id: string;
    source_id: string;
    fetched_at: string;
    text_source: string;
    completeness: { complete: boolean; missing_fields?: string[]; notes?: string | null };
    raw_artifact?: {
      id: string;
      state: 'present' | 'expired' | 'missing';
      media_type: string;
      size_bytes: number;
      expires_at?: string | null;
    };
  }>;
}

export interface StatusSnapshot {
  generated_at: string;
  timezone: string;
  uptime_seconds: number;
  components: Array<{ name: string; status: 'ok' | 'degraded' | 'down'; detail?: string }>;
  sources: Array<{
    source_id: string;
    name: string;
    enabled: boolean;
    last_status?: CollectionStatus;
    last_run_at?: string | null;
    last_success_at?: string | null;
    stale_minutes: number;
    stale: boolean;
    last_error?: string | null;
  }>;
  jobs: {
    outbox_backlog: number;
    open_failures: number;
    failures?: Array<{ id: string; consumer: string; event_type: string; error: string; last_failed_at: string }>;
  };
  storage: { backend: string; files: number; bytes: number };
  editions: Array<{
    edition_date: string;
    edition_type: string;
    /** Slot key and label; absent from backends before the slot schedule. */
    slot?: string;
    label?: string;
    state?: 'published' | 'due' | 'missed' | 'upcoming';
    quiet?: boolean;
    partial?: boolean;
    preparation_ms?: number;
    lead_ms?: number;
    published_at?: string | null;
    on_time: boolean;
    deadline: string;
    entries: number;
  }>;
  notifications: { sent_24h: number; suppressed_24h: number; unknown_24h: number; failed_24h: number };
  warnings: string[];
}

export interface SearchMeta {
  /** Publication time of the story's sources; null when no source stated one. */
  published_at: string | null;
  /** Event occurrences only: the start, or local midnight of a date-only event. */
  event_at?: string | null;
  /** The latest substantive update (newest version). */
  updated_at: string;
  first_seen_at: string;
  /** Ingestion time. Never shown as a publication time. */
  collected_at: string;
  sources: Array<{ id: string; name: string }>;
  group_key: string | null;
  group_label: string | null;
  rank: number | null;
  version_count: number;
}

export interface SearchResult extends CardView {
  search: SearchMeta;
}

export type SearchSort = 'relevance' | 'newest' | 'oldest' | 'updated' | 'source';
export type SearchGroup = 'none' | 'topic' | 'source' | 'age';
export type SearchDateField = 'published' | 'updated' | 'event';

export interface SearchParams {
  q?: string;
  source?: string[];
  topic?: string[];
  section?: string;
  from?: string;
  to?: string;
  date_field?: SearchDateField;
  sort?: SearchSort;
  group?: SearchGroup;
  limit?: number;
  offset?: number;
  as_of?: string;
}

export interface SearchResponse {
  results: SearchResult[];
  total: number;
  limit: number;
  offset: number;
  next_offset: number | null;
  as_of: string;
  query: string;
  sort: SearchSort;
  group: SearchGroup;
  date_field: SearchDateField;
  facets: {
    sources: Array<{ id: string; name: string; count: number; role?: SourceRole }>;
    topics: Array<{ id: string; label: string; count: number }>;
  };
}

export interface CollectionRun {
  id: string;
  source_id: string;
  started_at: string;
  completed_at: string;
  status: CollectionStatus;
  error?: string | null;
  warnings?: string[] | null;
  item_count: number;
}

export interface SourceInfo {
  id: string;
  name: string;
  /** Absent means journalism. */
  role?: SourceRole;
  kind: string;
  section: string;
  language?: string;
  privacy_class: string;
  enabled: boolean;
  retention_days: number;
  config?: { topic?: string; feed_url?: string } & Record<string, unknown>;
  last_run?: CollectionRun | null;
}

/** Serialises search parameters; repeatable filters become repeated keys. */
export function searchQueryString(params: SearchParams): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) for (const item of v) qs.append(k, String(item));
    else qs.set(k, String(v));
  }
  return qs.toString();
}

// ---------------------------------------------------------------- endpoints

/*
 * The Go API encodes an empty list as `null`. Lists are normalised here, once,
 * so no screen can crash on an empty day or mistake it for missing data.
 */
const list = <T>(v: T[] | null | undefined): T[] => v ?? [];

function normaliseBriefing(v: BriefingView): BriefingView {
  return {
    ...v,
    sections: list(v.sections).map((s) => ({ ...s, cards: list(s.cards).map(normaliseCard) })),
    more: list(v.more).map(normaliseCard),
  };
}

function normaliseCard<T extends CardView>(cv: T): T {
  return { ...cv, version: { ...cv.version, evidence: list(cv.version?.evidence) } };
}

export const api = {
  me: () => request<Me>('/me'),
  logout: () => request<{ status: string }>('/auth/logout', { method: 'POST' }),

  currentBriefing: () => request<BriefingView>('/briefings/current').then(normaliseBriefing),
  briefing: (id: string) => request<BriefingView>(`/briefings/${id}`).then(normaliseBriefing),
  briefingHistory: (limit = 30, offset = 0, date?: string) =>
    request<{ briefings: Briefing[] }>(`/briefings?limit=${limit}&offset=${offset}${date ? `&date=${date}` : ''}`).then((r) => ({
      ...r,
      briefings: list(r.briefings),
    })),
  editionDay: (date?: string) =>
    request<EditionDay>(`/editions${date ? `?date=${date}` : ''}`).then((r) => ({ ...r, slots: list(r.slots), dates: list(r.dates) })),

  cardVersion: (versionId: string) => request<CardView>(`/cards/versions/${versionId}`).then(normaliseCard),
  cardVersions: (cardId: string) =>
    request<{ card: Card; versions: CardVersion[] }>(`/cards/${cardId}/versions`).then((r) => ({ ...r, versions: list(r.versions) })),
  cardEvidence: (versionId: string) =>
    request<EvidenceView>(`/cards/versions/${versionId}/evidence`).then((r) => ({
      ...r,
      evidence: list(r.evidence),
      revisions: list(r.revisions),
    })),

  /** Changes only the fields present in the body. */
  saveFeedback: (body: {
    card_version_id: string;
    briefing_id?: string | null;
    interest?: Interest | null;
    quality?: Quality | null;
    quality_reasons?: QualityReason[] | null;
    note?: string | null;
  }) =>
    request<FeedbackResult>('/feedback', { method: 'POST', body: JSON.stringify(body) }).then((r) => ({
      ...r,
      interest_effect: list(r.interest_effect),
    })),

  cardChanges: (versionId: string, edition?: string | null) =>
    request<ChangesView>(`/cards/versions/${versionId}/changes${edition ? `?edition=${encodeURIComponent(edition)}` : ''}`),

  qualityCases: (status = '', reason = '') =>
    request<{ cases: QualityCase[]; counts: Record<string, number> }>(
      `/ops/quality-cases?status=${encodeURIComponent(status)}&reason=${encodeURIComponent(reason)}`,
    ).then((r) => ({ cases: list(r.cases), counts: r.counts ?? {} })),
  qualityCase: (id: string) =>
    request<QualityCaseDetail>(`/ops/quality-cases/${id}`).then((r) => ({ ...r, card: normaliseCard(r.card), sources: list(r.sources) })),
  reviewQualityCase: (id: string, body: { status: QualityCaseStatus; resolution: string | null }) =>
    request<QualityCase>(`/ops/quality-cases/${id}/review`, { method: 'POST', body: JSON.stringify(body) }),
  qualityFixture: (id: string, body: { required_terms: string[]; forbidden_terms: string[] }) =>
    request<{ path: string; fixture: unknown }>(`/ops/quality-cases/${id}/fixture`, { method: 'POST', body: JSON.stringify(body) }),

  subjectLookup: (q: string) =>
    request<{ lookup: LookupResult; existing: Record<string, string> }>(`/subjects/lookup?q=${encodeURIComponent(q)}`).then((r) => ({
      existing: r.existing ?? {},
      lookup: { ...r.lookup, proposals: list(r.lookup.proposals), registries: list(r.lookup.registries) },
    })),
  subjects: () => request<{ subjects: SubjectSummary[] }>('/subjects').then((r) => ({ subjects: list(r.subjects).map(normaliseSubject) })),
  createSubject: (body: { entity?: EntityInput; entity_id?: string }) =>
    request<SubjectSummary>('/subjects', { method: 'POST', body: JSON.stringify(body) }).then(normaliseSubject),
  subject: (id: string) =>
    request<SubjectPage>(`/subjects/${id}`).then((r) => ({
      ...r,
      subject: normaliseSubject(r.subject),
      main: list(r.main).map((x) => ({ ...x, card: normaliseCard(x.card) })),
      mentions: list(r.mentions).map((x) => ({ ...x, card: normaliseCard(x.card) })),
      unresolved: list(r.unresolved).map((x) => ({ ...x, card: normaliseCard(x.card) })),
    })),
  updateSubject: (id: string, body: EntityInput) =>
    request<SubjectSummary>(`/subjects/${id}`, { method: 'PUT', body: JSON.stringify(body) }).then(normaliseSubject),
  setSubjectFollow: (id: string, body: { status?: 'active' | 'paused'; include_related?: string[]; telegram?: boolean }) =>
    request<SubjectSummary>(`/subjects/${id}/follow`, { method: 'PUT', body: JSON.stringify(body) }).then(normaliseSubject),
  stopSubject: (id: string) => request<{ status: string }>(`/subjects/${id}/follow`, { method: 'DELETE' }),
  addRelation: (id: string, body: { relation: RelationKind; related_entity_id?: string; new_entity?: EntityInput }) =>
    request<EntityRelation>(`/subjects/${id}/relations`, { method: 'POST', body: JSON.stringify(body) }),
  removeRelation: (id: string, relationId: string) =>
    request<{ status: string }>(`/subjects/${id}/relations/${relationId}`, { method: 'DELETE' }),
  decideMatch: (id: string, matchId: string, role: 'main' | 'mention' | 'rejected') =>
    request<{ status: string }>(`/subjects/${id}/matches/${matchId}`, { method: 'POST', body: JSON.stringify({ role }) }),
  trainingHistory: (from?: string, to?: string) =>
    request<{ from: string; to: string; days: HistoryDay[] }>(
      `/crossfit/history${from || to ? `?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) }).toString()}` : ''}`,
    ).then((r) => ({ ...r, days: list(r.days).map((d) => ({ ...d, classes: list(d.classes), captures: list(d.captures) })) })),
  entities: (q = '') =>
    request<{ entities: Entity[] }>(`/entities?q=${encodeURIComponent(q)}`).then((r) => ({ entities: list(r.entities).map(normaliseEntity) })),

  follows: (includeStopped = false) =>
    request<{ follows: Follow[] }>(`/follows?include_stopped=${includeStopped}`).then((r) => ({ ...r, follows: list(r.follows) })),
  createFollow: (body: { subject_type: string; subject_id: string; label: string }) =>
    request<Follow>('/follows', { method: 'POST', body: JSON.stringify(body) }),
  stopFollow: (id: string) => request<{ status: string }>(`/follows/${id}`, { method: 'DELETE' }),
  followEvents: (id: string) =>
    request<{ follow: Follow; events: FollowEvent[] }>(`/follows/${id}/events`).then((r) => ({ ...r, events: list(r.events) })),

  search: (params: SearchParams, init?: RequestInit) =>
    request<SearchResponse>(`/history/search?${searchQueryString(params)}`, init).then((r) => ({
      ...r,
      results: list(r.results).map(normaliseCard),
      facets: { sources: list(r.facets?.sources), topics: list(r.facets?.topics) },
    })),

  sources: () => request<{ sources: SourceInfo[] }>('/sources').then((r) => ({ sources: list(r.sources) })),

  status: () =>
    request<StatusSnapshot>('/ops/status').then((r) => ({
      ...r,
      components: list(r.components),
      sources: list(r.sources),
      editions: list(r.editions),
      warnings: list(r.warnings),
      jobs: { ...r.jobs, failures: list(r.jobs?.failures) },
    })),
};

function normaliseEntity(e: Entity): Entity {
  return { ...e, aliases: list(e.aliases), identifiers: list(e.identifiers), instruments: list(e.instruments), relations: list(e.relations) };
}

function normaliseSubject(s: SubjectSummary): SubjectSummary {
  return { ...s, entity: normaliseEntity(s.entity), counts: s.counts ?? {} };
}

export const loginUrl = (redirect: string) =>
  `/api/v1/auth/login?redirect=${encodeURIComponent(redirect)}`;
